import { ContentStructure } from '../../services/enhancedContentStructureService';

// Extended node type for tree operations
export interface CSNode extends ContentStructure {
  children: CSNode[];
  level: number; // 0 = root, 1 = topic, 2 = chapter, 3 = unit, 4 = lesson
}

/**
 * Builds a hierarchical tree from flat ContentStructure array
 * @param structures - Flat array of ContentStructure objects
 * @returns Nested tree structure with children
 */
export function buildTree(structures: ContentStructure[]): CSNode[] {
  const structureMap = new Map<number, CSNode>();
  const roots: CSNode[] = [];

  // First pass: create all nodes
  structures.forEach(structure => {
    structureMap.set(structure.id, {
      ...structure,
      children: [],
      level: 0 // Will be calculated in next pass
    });
  });

  // Second pass: build parent-child relationships and calculate levels
  structures.forEach(structure => {
    const node = structureMap.get(structure.id)!;
    
    if (structure.parent_id === null) {
      // Root level structure
      node.level = 0;
      roots.push(node);
    } else if (structure.parent_id !== null && structure.parent_id !== undefined) {
      // Child structure
      const parent = structureMap.get(structure.parent_id);
      if (parent) {
        parent.children.push(node);
        node.level = parent.level + 1;
      } else {
        // Orphaned child, treat as root
        node.level = 0;
        roots.push(node);
      }
    } else {
      // No parent_id, treat as root
      node.level = 0;
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Filters a tree based on search term and filters
 * @param tree - Tree structure to filter
 * @param searchTerm - Search term for name/description
 * @param filterLevel - Level filter ('all', 'topic', 'chapter', 'unit', 'lesson')
 * @param filterSubject - Subject ID filter
 * @param filterGrade - Grade ID filter
 * @returns Filtered tree structure
 */
export function filterTree(
  tree: CSNode[],
  searchTerm: string = '',
  filterSubject: string = 'all',
  filterGrade: string = 'all'
): CSNode[] {
  const filterNode = (node: CSNode): CSNode | null => {
    // Check if node matches filters
    const matchesSearch = !searchTerm || 
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (node.description && node.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSubject = filterSubject === 'all' || 
      node.subject_id?.toString() === filterSubject;
    
    const matchesGrade = filterGrade === 'all' || 
      node.grade_id?.toString() === filterGrade;

    if (!matchesSearch || !matchesSubject || !matchesGrade) {
      return null;
    }

    // Filter children recursively
    const filteredChildren = node.children
      .map(child => filterNode(child))
      .filter((child): child is CSNode => child !== null);

    // Return node with filtered children
    return {
      ...node,
      children: filteredChildren
    };
  };

  return tree
    .map(node => filterNode(node))
    .filter((node): node is CSNode => node !== null);
}

/**
 * Gets the display name for a level
 * @param level - Numeric level (0-4)
 * @returns Human-readable level name
 */
export function getLevelName(level: number): string {
  const levelNames = ['Structure', 'Topic', 'Chapter', 'Unit', 'Lesson'];
  return levelNames[level] || 'Unknown';
}

/**
 * Gets the color for a level
 * @param level - Numeric level (0-4)
 * @returns CSS color value
 */
export function getLevelColor(level: number): string {
  const colors = [
    '#6b7280', // Structure - gray
    '#3b82f6', // Topic - blue
    '#10b981', // Chapter - green
    '#f59e0b', // Unit - orange
    '#8b5cf6'  // Lesson - purple
  ];
  return colors[level] || '#6b7280';
}

/**
 * Gets the abbreviation for a level
 * @param level - Numeric level (0-4)
 * @returns Short abbreviation
 */
export function getLevelAbbr(level: number): string {
  const abbreviations = ['S', 'T', 'C', 'U', 'L'];
  return abbreviations[level] || '?';
}
